import {Button} from '@components/ui/button'
import {formatLocaleDate} from '@/dates.ts'
import {FC, ReactElement, useEffect, useState} from 'react'
import {useTranslation} from '@/translations/i18n'
import {CalendarProps, Calendar} from '@components/commons/calendar'
import {Popover, PopoverContent, PopoverTrigger} from '@components/ui/popover'
import {useMediaQuery} from '@hooks/use-media-query'

interface FilterDatePickerProps {
    values: Date[]
    onConfirm: (values: Date[]) => void
    trigger: ReactElement
    calendarProps?: Partial<CalendarProps>
}

export const FilterDatePicker: FC<FilterDatePickerProps> = ({values, trigger, onConfirm, calendarProps}) => {
    const {t} = useTranslation()
    const [selectedDates, setSelectedDates] = useState<Date[]>(values)
    const [open, setOpen] = useState(false)
    const isLarge = useMediaQuery({breakpoint: 'md'})
    const onSetDate = (values: Date[]) => {
        onConfirm(values)
        setSelectedDates(values)
        setOpen(false)
    }

    useEffect(() => {
        if (values.length) {
            setSelectedDates(values)
        }
    }, [values])

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger render={trigger} />
            <PopoverContent
                sideOffset={8}
                className="z-modal-dropdown max-h-none overflow-visible rounded-sm border-0 bg-white shadow-md"
            >
                <div className="grid grid-rows-[auto_minmax(0,1fr)] md:grid-rows-[auto]">
                    <div className="flex flex-col">
                        <Calendar
                            numMonths={isLarge ? 2 : 1}
                            onDatesChange={dates => setSelectedDates(dates)}
                            mode={'range'}
                            selectedDates={selectedDates}
                            withLeadingZeros={false}
                            selectedDatesComponent={
                                !isLarge && (
                                    <div className="flex w-full items-center gap-3 py-2">
                                        {selectedDates[0] && (
                                            <div className="w-full rounded-lg px-3 py-2 text-center shadow-xs outline-1 -outline-offset-1 outline-neutral-300 md:w-auto md:px-4 md:py-2.5 md:text-left">
                                                {formatLocaleDate(selectedDates[0], 'PP')}
                                            </div>
                                        )}
                                        –
                                        {selectedDates[1] && (
                                            <div className="w-full rounded-lg px-3 py-2 text-center shadow-xs outline-1 -outline-offset-1 outline-neutral-300 md:w-auto md:px-4 md:py-2.5 md:text-left">
                                                {formatLocaleDate(selectedDates[1], 'PP')}
                                            </div>
                                        )}
                                    </div>
                                )
                            }
                            {...calendarProps}
                        />
                        <div className="flex w-full flex-col justify-between gap-2 border-t border-neutral-200 p-4 [&_button]:w-full md:flex-row md:[&_button]:w-auto">
                            {isLarge && (
                                <div className="flex items-center gap-3">
                                    {selectedDates[0] && (
                                        <div className="w-full rounded-lg px-3 py-2 text-center shadow-xs  outline-1 -outline-offset-1 outline-neutral-300 md:w-auto md:px-4 md:py-2.5 md:text-left">
                                            {formatLocaleDate(selectedDates[0], 'PP')}
                                        </div>
                                    )}
                                    –
                                    {selectedDates[1] && (
                                        <div className="w-full rounded-lg px-3 py-2 text-center shadow-xs  outline-1 -outline-offset-1 outline-neutral-300 md:w-auto md:px-4 md:py-2.5 md:text-left">
                                            {formatLocaleDate(selectedDates[1], 'PP')}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <Button
                                    variant={'secondary'}
                                    onClick={() => {
                                        setSelectedDates(values)
                                        setOpen(false)
                                    }}
                                >
                                    {t('commons:cancel')}
                                </Button>
                                <Button
                                    onClick={() => {
                                        onSetDate(selectedDates)
                                    }}
                                    disabled={selectedDates.length < 2}
                                >
                                    {t('transactions:apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}
